import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "../../commons/Sidebar/Sidebar";
import Header from "../../commons/Header/Header";
import { useLocation } from "react-router-dom";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "notosans-fontface/css/notosans-fontface.css";
import { EditorState, ContentState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import DOMPurify from "dompurify";
import he from "he";
import { toast } from "react-toastify";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { UI_CONSTANTS } from "../../utils/constants";
import {
  CMS_ABOUT_US_ROLE,
  CMS_ABOUT_US_TAB,
  CMS_CONTENT,
  CMS_FAQ_TAB,
  CMS_ROLE_CONTENT_TABS,
  CMS_ROLE_TABS,
  buildCmsQueryParams,
  buildCmsPayload,
  getCmsContentByType,
} from "../../utils/cmsContent";
import FaqList from "./FaqList";

const EDITOR_TOOLBAR = {
  options: ["inline", "fontSize", "list", "textAlign", "link"],
  inline: { options: ["bold", "italic", "underline", "strikethrough"] },
  list: { options: ["unordered", "ordered"] },
  fontSize: { options: [8, 10, 12, 14, 16, 18, 24, 30, 36] },
};

const htmlToEditorState = (html) => {
  const decoded = he.decode(html || "");
  const contentBlock = htmlToDraft(decoded);
  if (!contentBlock) return EditorState.createEmpty();
  const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
  return EditorState.createWithContent(contentState);
};

const editorStateToHtml = (editorState) => {
  const raw = convertToRaw(editorState.getCurrentContent());
  return DOMPurify.sanitize(draftToHtml(raw));
};

function ContentManagementSystem() {
  const location = useLocation();
  const [roleTabId, setRoleTabId] = useState(CMS_ROLE_TABS[0].id);
  const [contentType, setContentType] = useState(CMS_ROLE_CONTENT_TABS[0].type);
  const [showAboutUs, setShowAboutUs] = useState(false);
  const [showFaq, setShowFaq] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [viewHtml, setViewHtml] = useState("");
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());

  const roleTab = CMS_ROLE_TABS.find((t) => t.id === roleTabId) || CMS_ROLE_TABS[0];
  const role = showAboutUs ? CMS_ABOUT_US_ROLE : roleTab.role;
  const activeContentType = showAboutUs ? CMS_CONTENT.ABOUT_US.type : contentType;
  const contentConfig = getCmsContentByType(activeContentType);

  useEffect(() => {
    const state = location.state;
    if (state?.roleTab) setRoleTabId(state.roleTab);
    if (state?.contentTab) setContentType(state.contentTab);
    if (state?.showAboutUs) setShowAboutUs(true);
    if (state?.showFaq) setShowFaq(true);
  }, [location.state]);

  const fetchCms = useCallback(async () => {
    if (!role || !activeContentType || showFaq) return;

    setLoading(true);
    setError(false);
    try {
      const params = new URLSearchParams(buildCmsQueryParams(role, activeContentType));
      const response = await handleApiCall("get", `${endpoints.cms}?${params}`);

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        const item = response?.data?.data;
        const description = item?.description || item?.content || "";
        const decoded = he.decode(description);
        setViewHtml(DOMPurify.sanitize(decoded));
      } else {
        setViewHtml("");
      }
    } catch (err) {
      console.error(`Error fetching ${contentConfig?.label}:`, err);
      setError(true);
      setViewHtml("");
    } finally {
      setLoading(false);
    }
  }, [role, activeContentType, contentConfig?.label, showFaq]);

  useEffect(() => {
    setIsEditing(false);
    fetchCms();
  }, [fetchCms]);

  const handleRoleChange = (id) => {
    setShowAboutUs(false);
    setShowFaq(false);
    setRoleTabId(id);
    setIsEditing(false);
  };

  const handleAboutUsClick = () => {
    setShowAboutUs(true);
    setShowFaq(false);
    setIsEditing(false);
  };

  const handleContentTypeChange = (type) => {
    setShowAboutUs(false);
    setShowFaq(false);
    setContentType(type);
    setIsEditing(false);
  };

  const handleFaqClick = () => {
    setShowAboutUs(false);
    setShowFaq(true);
    setIsEditing(false);
  };

  const startEdit = () => {
    setEditorState(htmlToEditorState(viewHtml));
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditorState(EditorState.createEmpty());
  };

  const handleSave = async () => {
    const cleanHtml = editorStateToHtml(editorState);
    const payload = buildCmsPayload(role, activeContentType, cleanHtml);

    setSaving(true);
    try {
      const response = await handleApiCall("post", endpoints.addCms, payload);

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        toast.success("Content updated successfully!");
        setViewHtml(cleanHtml);
        setIsEditing(false);
      } else {
        toast.error(response?.data?.message || "Failed to save content");
      }
    } catch (err) {
      console.error("Error saving content:", err);
      toast.error("Failed to save content");
    } finally {
      setSaving(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-5">
          <p className="text-danger">Failed to load {contentConfig?.label}</p>
          <button type="button" className="btn btn-primary" onClick={fetchCms}>
            Retry
          </button>
        </div>
      );
    }

    if (isEditing) {
      return (
        <>
          <div className="cms-editor mt-2">
            <div className="form-group" style={{ width: "100%" }}>
              <Editor
                editorState={editorState}
                onEditorStateChange={setEditorState}
                toolbar={EDITOR_TOOLBAR}
              />
            </div>
          </div>
          <div className="d-flex justify-content-end mt-3">
            <button
              type="button"
              onClick={cancelEdit}
              className="btn btn-light mr-2"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary bordered weight-300"
              onClick={handleSave}
              disabled={saving}
            >
              <b>{saving ? "Saving..." : "Update"}</b>
            </button>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="d-flex justify-content-end mb-3">
          <button type="button" className="btn btn-primary bordered weight-300" onClick={startEdit}>
            <b>Edit</b>
          </button>
        </div>
        <div className="cms-content-view__body">
          {viewHtml ? (
            <div dangerouslySetInnerHTML={{ __html: viewHtml }} />
          ) : (
            <p className="mb-0">No {contentConfig?.label} found. Click Edit to add content.</p>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar status={true} />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-4">
              <h3 className="text-default weight-400">Content Management System</h3>
            </div>
            <div className="row mx-0 main-content-body">
              <div className="col-md-12 px-0 cms-tabs-row">
                <ul className="nav common-tabs cms-tabs w-100" role="tablist">
                  {CMS_ROLE_TABS.map((tab, index) => (
                    <li className="nav-item" key={tab.id}>
                      <a
                        className={`nav-link br-10 w-auto text-center ${index === 0 ? "ml-0" : ""} ${
                          !showAboutUs && !showFaq && roleTabId === tab.id ? "active" : ""
                        }`}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleRoleChange(tab.id)}
                      >
                        {tab.label}
                      </a>
                    </li>
                  ))}
                  <li className="nav-item">
                    <a
                      className={`nav-link br-10 w-auto text-center ${
                        showAboutUs ? "active" : ""
                      }`}
                      style={{ cursor: "pointer" }}
                      onClick={handleAboutUsClick}
                    >
                      {CMS_ABOUT_US_TAB.label}
                    </a>
                  </li>
                </ul>
              </div>
              {!showAboutUs && (
                <div className="col-md-12 px-0 cms-tabs-row">
                  <ul className="nav common-tabs cms-tabs w-100" role="tablist">
                    {CMS_ROLE_CONTENT_TABS.map((tab, index) => (
                      <li className="nav-item" key={tab.type}>
                        <a
                          className={`nav-link br-10 w-auto text-center ${index === 0 ? "ml-0" : ""} ${
                            !showFaq && contentType === tab.type ? "active" : ""
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleContentTypeChange(tab.type)}
                        >
                          {tab.label}
                        </a>
                      </li>
                    ))}
                    <li className="nav-item">
                      <a
                        className={`nav-link br-10 w-auto text-center ${showFaq ? "active" : ""}`}
                        style={{ cursor: "pointer" }}
                        onClick={handleFaqClick}
                      >
                        {CMS_FAQ_TAB.label}
                      </a>
                    </li>
                  </ul>
                </div>
              )}
              <div className="col-md-12 tab-content row px-0 mx-0 cms-content-below-tabs">
                {showFaq ? (
                  <div className="col-md-12 px-0 mb-4 py-2">
                    <FaqList role={role} />
                  </div>
                ) : showAboutUs || contentType ? (
                  <div className="card mb-4 px-4 py-4 cms-content-view" style={{ width: "100%" }}>
                    {renderContent()}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContentManagementSystem;
